import { sendPasswordResetEmail, signInWithEmailAndPassword } from 'firebase/auth';
import { useState, type FormEvent } from 'react';
import { api, auth, isDuoUid } from '../firebase';
import { LANGS, getLang, setLang } from '../lib/i18n';
import { FOREIGN_KEY } from '../state';
import { t } from '../lib/i18n';

const ERRORS: Record<string, string> = {
  'auth/invalid-credential': 'Email or password is wrong.',
  'auth/invalid-email': 'That email address does not look right.',
  'auth/too-many-requests': 'Too many attempts. Wait a minute and try again.',
  'auth/network-request-failed': 'No connection. Check your internet and try again.',
};

function message(e: unknown): string {
  const err = e as { code?: string; message?: string };
  if (err.code && ERRORS[err.code]) return t(ERRORS[err.code]);
  // Callable errors carry a readable message from the signUp function.
  if (err.code?.startsWith('functions/') && err.message) return t(err.message);
  return t('Something went wrong. Try again.');
}

export default function Login() {
  const [mode, setMode] = useState<'in' | 'up'>('in');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  let foreign = false;
  try { foreign = sessionStorage.getItem(FOREIGN_KEY) === '1'; } catch { /* storage blocked */ }
  const [error, setError] = useState(foreign ? t('That account belongs to another app. Create a DuoPlate account with a different email.') : '');
  const [info, setInfo] = useState('');

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(''); setInfo('');
    try { sessionStorage.removeItem(FOREIGN_KEY); } catch { /* ignore */ }
    try {
      if (mode === 'up') {
        if (password.length < 8) { setError(t('Use at least 8 characters for the password.')); return; }
        await api.signUp({ email: email.trim(), password, displayName: name.trim() });
      }
      const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
      if (!isDuoUid(cred.user.uid)) setError(t('That account belongs to another app. Create a DuoPlate account with a different email.'));
    } catch (err) {
      // After "Create account", a failed sign-in means the email was already taken (the server
      // does not say so, to avoid revealing who uses which app).
      if (mode === 'up' && (err as { code?: string }).code === 'auth/invalid-credential') {
        setError(t('This email already has an account. Sign in, reset your password, or use a different email.'));
      } else {
        setError(message(err));
      }
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    setError(''); setInfo('');
    if (!email.trim()) { setError(t('Type your email above first.')); return; }
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setInfo(t('If that email has an account, a reset link is on its way.'));
    } catch (err) {
      setError(message(err));
    }
  }

  return (
    <main className="auth">
      <div className="seg lang-pick" role="group" aria-label="Language / Dil">
        {LANGS.map((l) => <button key={l.id} type="button" aria-pressed={getLang() === l.id} onClick={() => setLang(l.id)}>{l.label}</button>)}
      </div>
      <div className="auth-brand">
        <img src="/icons/icon-192.png" alt="" width={56} height={56} />
        <h1>Duo<span>Plate</span></h1>
        <p className="tagline">{t('Make your day with your partner.')}</p>
        <p className="muted small">{getLang() === 'tr' ? 'Make your day with your partner.' : 'Gününü partnerinle planla.'}</p>
        <p className="muted">{t('Meals, routines and date nights, planned together, with a coach that adjusts the plan for both of you. Works on your own too.')}</p>
      </div>

      <form className="card stack" onSubmit={submit}>
        <div className="seg" role="tablist">
          <button type="button" role="tab" aria-selected={mode === 'in'} onClick={() => setMode('in')}>{t('Sign in')}</button>
          <button type="button" role="tab" aria-selected={mode === 'up'} onClick={() => setMode('up')}>{t('Create account')}</button>
        </div>
        {mode === 'up' && (
          <label className="field">{t('Your first name')}
            <input id="name" autoComplete="given-name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={40} />
          </label>
        )}
        <label className="field">{t('Email')}
          <input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        <label className="field">{t('Password')}
          <input id="password" type="password" autoComplete={mode === 'up' ? 'new-password' : 'current-password'} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={mode === 'up' ? 8 : undefined} />
        </label>
        {error && <p className="error" role="alert">{error}</p>}
        {info && <p className="ok" role="status">{info}</p>}
        <button className="btn primary" disabled={busy}>{busy ? t('One moment…') : mode === 'up' ? t('Create account') : t('Sign in')}</button>
        {mode === 'in' && <button type="button" className="link" onClick={reset}>{t('Forgot your password?')}</button>}
      </form>
      <p className="muted small center">{t('How we handle your data:')} <a href="/privacy">{t('privacy notice (Aydınlatma Metni)')}</a>.</p>
    </main>
  );
}
